import { useState, useEffect, useRef, Dispatch, SetStateAction, ChangeEvent, memo, useCallback } from 'react';
import { Form } from 'react-final-form';
import { range } from 'ramda';

import { LegalBasisSearchFieldsEnum } from 'APIPackage/CatalogsModule/enums/LegalBasisSearchFieldsEnum';
import { IKnmLegalBasisParams } from 'APIPackage/KnmModule/interfaces/IKnmLegalBasisParams';
import { ILegalBasisDocumentExtended } from 'APIPackage/CatalogsModule/interfaces/ILegalBasisDocumentExtended';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { generateYearsSelectOptions } from 'CorePackage/MainModule/common/generateYearsSelectOptions';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';

import { SelectLegalBases } from './SelectLegalBases';
import { useLegalBasisLevels } from '../hooks/useLegalBasisLevels';
import { useLegalBasisSearch } from '../hooks/useLegalBasisSearch';
import { useLegalBasisTypes } from '../hooks/useLegalBasisTypes';
import styles from './LegalBasesForm.module.scss';
import { initialLegalBasesFormValues } from '../constants/initialLegalBasesFormValues';
import { ILegalBasesForm } from '../interfaces/ILegalBasesForm';
import { LegalBasesFormRenderProps } from '../types/LegalBasesFormRenderProps';

const PAGE_SIZE = '15';

const currentYear = new Date().getFullYear();
const yearsArray = range(1917, currentYear + 1).reverse();
const yearsSelectOptions: IYearSelectOption[] = generateYearsSelectOptions(yearsArray);

interface ILegalBasesFormProps {
  active?: boolean;
  selectedLegalBases: IKnmLegalBasisParams[];
  frequentlyUsedDocuments?: ILegalBasisDocumentExtended[];
  setSelectedLegalBases: Dispatch<SetStateAction<IKnmLegalBasisParams[]>>;
  onFormRender: (form: LegalBasesFormRenderProps['form'], resetForm: () => void) => void;
}

export const LegalBasesForm = memo(
  ({
    active,
    selectedLegalBases,
    frequentlyUsedDocuments,
    setSelectedLegalBases,
    onFormRender,
  }: ILegalBasesFormProps) => {
    const didVisibleRef = useRef(active);

    if (active) {
      didVisibleRef.current = true;
    }
    const [isLegalBasesExists, setIsLegalBasesExists] = useState(true);
    const [currentLegalBasesDocuments, setCurrentLegalBasesDocuments] = useState<ILegalBasisDocumentExtended[]>([]);
    const { legalBasisLevelsSelectOptions } = useLegalBasisLevels(didVisibleRef.current);
    const { legalBasisDocumentTypesSelectOptions } = useLegalBasisTypes(didVisibleRef.current);
    const {
      searchLegalBasisDocuments,
      getSearchLegalBasisDocumentsNextPage,
      resetSearchLegalBasisDocuments,
      legalBasisDocuments,
      page,
      pageCount,
    } = useLegalBasisSearch();

    useEffect(() => {
      legalBasisDocuments && setCurrentLegalBasesDocuments(legalBasisDocuments);
    }, [legalBasisDocuments]);

    const handleFormSubmit = useCallback(
      (values: ILegalBasesForm) => {
        if (isLegalBasesExists && values.searchString && values.searchField) {
          searchLegalBasisDocuments({
            pagination: {
              size: PAGE_SIZE,
            },
            searchString: values.searchString,
            searchField: values.searchField,
            ...(values.level?.id && { levelId: values.level?.id }),
            ...(values.type?.id && { typeId: values.type?.id }),
            ...(values.searchDateType === 'year' && values.year?.value && { year: Number(values.year?.value) }),
            ...(values.searchDateType === 'date' &&
              values.date && { date: formatDate({ date: values.date, dateFormat: SYSTEM_DATE_FORMAT_FNS }) }),
          });
        }
      },
      [isLegalBasesExists, searchLegalBasisDocuments],
    );

    const handleLegalBasesExistsCheckboxChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => setIsLegalBasesExists(e.target.value === 'true'),
      [],
    );

    const handleFrequentlyUsedButtonClick = useCallback(
      (legalBasisDocument: ILegalBasisDocumentExtended) => () => {
        resetSearchLegalBasisDocuments();
        setCurrentLegalBasesDocuments([legalBasisDocument]);
      },
      [resetSearchLegalBasisDocuments],
    );

    function renderForm({ handleSubmit, form, values }: LegalBasesFormRenderProps) {
      const resetForm = () => {
        form.reset();
        setIsLegalBasesExists(true);
        resetSearchLegalBasisDocuments();
        setCurrentLegalBasesDocuments([]);
      };

      onFormRender(form, resetForm);

      return (
        <form onSubmit={handleSubmit}>
          {isLegalBasesExists && (
            <Row notWrap>
              <FieldBlock margin="right">
                <TextInputField id="searchString" name="searchString" />
              </FieldBlock>
              <Button
                text="Найти"
                color="primary"
                size="medium"
                type="submit"
                disabled={!values.searchString || values.searchString?.length < 3}
              />
            </Row>
          )}
          <Row notWrap notSpace>
            <FieldBlock size="content" margin="right">
              <Checkbox
                id="legalBasesExists"
                name="legalBasesExists"
                value="true"
                label="Выбрать из справочника"
                type="radio"
                theme="system"
                size="medium"
                checked={isLegalBasesExists}
                onChange={handleLegalBasesExistsCheckboxChange}
              />
            </FieldBlock>
            <FieldBlock size="content">
              <Checkbox
                id="legalBasesNotExist"
                name="legalBasesExists"
                value="false"
                label="Отсутствует в справочнике"
                type="radio"
                theme="system"
                size="medium"
                checked={!isLegalBasesExists}
                onChange={handleLegalBasesExistsCheckboxChange}
              />
            </FieldBlock>
          </Row>
          {isLegalBasesExists && (
            <>
              <Row notWrap notSpace>
                <FieldBlock size="content" margin="right">
                  <CheckboxField
                    id={LegalBasisSearchFieldsEnum.DOCUMENT_NAME}
                    name="searchField"
                    label="Название документа"
                    type="radio"
                    theme="system"
                    size="medium"
                    value={LegalBasisSearchFieldsEnum.DOCUMENT_NAME}
                  />
                </FieldBlock>
                <FieldBlock size="content" margin="right">
                  <CheckboxField
                    id={LegalBasisSearchFieldsEnum.DOCUMENT_AGENCY}
                    name="searchField"
                    label="Орган принятия документа"
                    type="radio"
                    theme="system"
                    size="medium"
                    value={LegalBasisSearchFieldsEnum.DOCUMENT_AGENCY}
                  />
                </FieldBlock>
                <FieldBlock size="content" margin="right">
                  <CheckboxField
                    id={LegalBasisSearchFieldsEnum.DOCUMENT_NUMBER}
                    name="searchField"
                    label="Номер документа"
                    type="radio"
                    theme="system"
                    size="medium"
                    value={LegalBasisSearchFieldsEnum.DOCUMENT_NUMBER}
                  />
                </FieldBlock>
                <FieldBlock size="content" margin="right">
                  <CheckboxField
                    id={LegalBasisSearchFieldsEnum.DOCUMENT_NUMBER_MINJUST}
                    name="searchField"
                    label="Номер документа (Минюст России)"
                    type="radio"
                    theme="system"
                    size="medium"
                    value={LegalBasisSearchFieldsEnum.DOCUMENT_NUMBER_MINJUST}
                  />
                </FieldBlock>
              </Row>
              <Row notWrap>
                <div className={styles.LevelTypeFields}>
                  <Row notSpace notWrap margin="none">
                    <FieldBlock label="Уровень" placementLabel="left" labelSize="col-4" color="black" margin="right">
                      <SelectInputField
                        id="legal-basis-level-select"
                        className="shared-row-field"
                        name="level"
                        size="medium"
                        options={legalBasisLevelsSelectOptions}
                      />
                    </FieldBlock>
                    <FieldBlock label="Тип" placementLabel="left" labelSize="col-4" color="black">
                      <SelectInputField
                        id="legal-basis-type-select"
                        className="shared-row-field"
                        name="type"
                        size="medium"
                        options={legalBasisDocumentTypesSelectOptions}
                      />
                    </FieldBlock>
                  </Row>
                </div>
                <div className={styles.DateFields}>
                  <Row notWrap notSpace margin="none">
                    <FieldBlock size="content" margin="right">
                      <CheckboxField
                        id="legal-basis-year-checkbox"
                        name="searchDateType"
                        label="Год принятия"
                        type="radio"
                        theme="system"
                        size="medium"
                        value="year"
                      />
                    </FieldBlock>
                    <FieldBlock margin="right">
                      <SelectInputField
                        className="shared-row-field"
                        id="legal-basis-year-select"
                        name="year"
                        size="medium"
                        options={yearsSelectOptions}
                        disabled={values.searchDateType !== 'year'}
                      />
                    </FieldBlock>
                    <FieldBlock size="content" margin="right">
                      <CheckboxField
                        id="legal-basis-date-checkbox"
                        name="searchDateType"
                        label="Дата принятия"
                        type="radio"
                        theme="system"
                        size="medium"
                        value="date"
                      />
                    </FieldBlock>
                    <FieldBlock>
                      <DatePickerField
                        id="legal-basis-datepicker"
                        name="date"
                        disabled={values.searchDateType !== 'date'}
                      />
                    </FieldBlock>
                  </Row>
                </div>
              </Row>
              <SelectLegalBases
                legalBasesDocuments={currentLegalBasesDocuments}
                selectedLegalBases={selectedLegalBases}
                setSelectedLegalBases={setSelectedLegalBases}
                onLoadButtonClick={page < pageCount ? getSearchLegalBasisDocumentsNextPage : undefined}
              />
              {frequentlyUsedDocuments && frequentlyUsedDocuments.length > 0 && (
                <div>
                  <h4 className={styles.FrequentlyUsedTitle}>Часто используемые значения</h4>
                  <div className={styles.FrequentlyUsedDocuments}>
                    {frequentlyUsedDocuments.map(document => (
                      <button
                        key={document.id}
                        className={styles.FrequentlyUsedButton}
                        type="button"
                        onClick={handleFrequentlyUsedButtonClick(document)}
                      >
                        {`№:${document.atrNumber}${
                          document.atrDate
                            ? ` от ${formatDate({ date: document.atrDate, dateFormat: DATE_FORMAT_LONG_FNS })}`
                            : ''
                        } ${document.documentName}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {!isLegalBasesExists && <TextareaField className={styles.Textarea} name="notExistLegalBasisText" />}
        </form>
      );
    }

    return (
      <Form<ILegalBasesForm>
        initialValues={initialLegalBasesFormValues}
        render={renderForm}
        onSubmit={handleFormSubmit}
      />
    );
  },
);
